import { FormControlLabel, FormGroup, Link, Typography } from '@mui/material';
import CheckBox from '@mui/material/Checkbox';
import { useSearchkit } from '@searchkit/client';
import React, { useMemo, useState } from 'react';
import { SkFacetSet, SkFacetSetEntry } from 'sr-types/lib/search/v1/graphql';
import { Conditional, V } from '../../../common/Layout';

export const CustomListFacet = ({
    facet,
    loading,
    showCount,
    autoApply,
    onFiltersChanged
}: {
    facet: SkFacetSet;
    loading: boolean;
    showCount: boolean;
    autoApply?: boolean;
    onFiltersChanged?: () => void;
}) => {
    const api = useSearchkit();
    const [seeMore, setSeeMore] = useState(false);
    const [selectedValues, setSelectedValues] = useState<string[]>(() => {
        const current = api.getFiltersByIdentifier(facet.identifier);
        return Array.isArray(current) ? current.map((f) => String(f.value)) : [];
    });

    const handleChange = (filter: { identifier: string; value: string }) => () => {
        const pageSize = api.getSearchState().page.size;
        const isSelected = selectedValues.includes(filter.value);
        if (isSelected) {
            api.removeFilter(filter);
            setSelectedValues((prev) => prev.filter((v) => v !== filter.value));
        } else {
            api.addFilter(filter);
            setSelectedValues((prev) => [...prev, filter.value]);
        }
        api.setPage({
            from: 0,
            size: pageSize
        });
        onFiltersChanged?.();
        if (autoApply) {
            api.search();
        }
    };

    const entries = useMemo(() => {
        return facet.entries
            .filter((entry: SkFacetSetEntry, idx) => (seeMore ? true : idx < 15))
            .map((entry: SkFacetSetEntry) => {
                const filter = { identifier: facet.identifier, value: entry.id.split('_')[1] };
                const checked = api.isFilterSelected(filter);
                return (
                    <FormControlLabel
                        key={entry.id}
                        componentsProps={{
                            typography: {
                                sx: { fontSize: 'small' }
                            }
                        }}
                        control={<CheckBox size={'small'} checked={checked} onChange={handleChange(filter)} />}
                        label={showCount ? `${entry.label} (${entry.count})` : entry.label}
                    />
                );
            });
    }, [facet.entries, facet.identifier, seeMore, showCount, selectedValues]);

    return (
        <V>
            <Typography sx={{ fontSize: 'small' }}>{facet.label}</Typography>
            <V>
                <FormGroup>{entries}</FormGroup>
                <Conditional condition={facet.entries?.length > 15}>
                    {() => (
                        <Link
                            className="clickable"
                            onClick={() => {
                                setSeeMore(!seeMore);
                            }}
                        >
                            {!seeMore ? 'See More' : 'See Less'}
                        </Link>
                    )}
                </Conditional>
            </V>
        </V>
    );
};

CustomListFacet.DISPLAY = 'CustomListFacet';
